import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { apiCall } from 'utils/apiClient';

const BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || '';

const AddPartner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editPartner = location?.state?.editPartner || null;
  const isEditMode = Boolean(editPartner);

  const getEmptyExistingDocs = () => ({
    aadhaar_front: null,
    aadhaar_back: null,
    pan_card: null,
    address_proof: null,
    photo: null,
  });

  const calculateAgeFromDob = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1;
    }
    return age > 0 ? String(age) : '';
  };

  const normalizeDobValue = (dob) => {
    if (!dob) return '';
    const rawDob = String(dob).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawDob)) return rawDob;

    const parsed = new Date(rawDob);
    if (Number.isNaN(parsed.getTime())) return '';

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getInitialStaffData = (prefillMobile = '', partner = null) => {
    const bankPayload = partner?.bank_details || {};

    return {
      name: '',
      mobile: prefillMobile,
      dob: '',
      work: '',
      labour_count: '',
      area: '',
      service_areas: '',
      aadhaar_no: '',
      aadhaar_front: null,
      aadhaar_back: null,
      pan_no: '',
      pan_card: null,
      address_proof: null,
      photo: null,
      account_holder_name: '',
      bank_name: '',
      bank_branch: '',
      account_number: '',
      ifsc_code: '',
      upi_id: '',
      ...(partner
        ? {
            name: partner?.name || '',
            mobile: partner?.mobile || prefillMobile,
            dob: normalizeDobValue(partner?.dob),
            work: partner?.profession || partner?.work || '',
            labour_count: partner?.team_size ? String(partner.team_size) : partner?.labour_count || '',
            area: partner?.area || '',
            service_areas: partner?.service_areas || partner?.area || '',
            aadhaar_no: partner?.aadhaar_no || partner?.aadhar_no || '',
            pan_no: partner?.pan_no || '',
            account_holder_name:
              partner?.account_holder_name ||
              partner?.account_name ||
              bankPayload?.account_holder_name ||
              bankPayload?.account_name ||
              '',
            bank_name: partner?.bank_name || bankPayload?.bank_name || '',
            bank_branch: partner?.bank_branch || bankPayload?.bank_branch || '',
            account_number:
              partner?.account_number || partner?.account_no || bankPayload?.account_number || bankPayload?.account_no || '',
            ifsc_code: partner?.ifsc_code || partner?.ifsc || bankPayload?.ifsc_code || bankPayload?.ifsc || '',
            upi_id: partner?.upi_id || bankPayload?.upi_id || '',
          }
        : {}),
    };
  };

  const [staffData, setStaffData] = useState(() =>
    getInitialStaffData(location?.state?.prefillMobile || '', editPartner)
  );
  const [errors, setErrors] = useState({});
  const [existingDocs, setExistingDocs] = useState(getEmptyExistingDocs);
  const [loadingExistingDocs, setLoadingExistingDocs] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mapDocTypeToField = (docType = '') => {
    const normalized = String(docType).toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');

    if (normalized.includes('aadhaar') || normalized.includes('aadhar')) {
      if (normalized.includes('front')) return 'aadhaar_front';
      if (normalized.includes('back')) return 'aadhaar_back';
    }
    if (normalized.includes('pan')) return 'pan_card';
    if (normalized.includes('address')) return 'address_proof';
    if (normalized.includes('photo') || normalized.includes('profile') || normalized.includes('selfie')) {
      return 'photo';
    }

    return null;
  };

  const buildFileUrl = (filePath = '') => {
    if (!filePath) return '';
    if (/^https?:\/\//i.test(filePath)) return filePath;

    const normalizedBase = String(BASE_URL || '').replace(/\/$/, '');
    const normalizedPath = String(filePath).startsWith('/') ? filePath : `/${filePath}`;
    return `${normalizedBase}${normalizedPath}`;
  };

  const isImageFile = (filePath = '') => {
    const cleanPath = String(filePath || '').split('?')[0].toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(cleanPath);
  };

  const renderExistingDoc = (field) => {
    if (!isEditMode) return null;
    if (loadingExistingDocs) {
      return <p className="mt-1 text-[10px] text-slate-500">Loading existing file...</p>;
    }

    const doc = existingDocs[field];
    if (!doc?.file_path) {
      return <p className="mt-1 text-[10px] text-slate-500">No existing file uploaded</p>;
    }

    const fileUrl = buildFileUrl(doc.file_path);
    const statusLabel = doc?.status ? String(doc.status).replace(/_/g, ' ') : '';

    if (!isImageFile(doc.file_path)) {
      return (
        <p className="mt-1 text-[10px] text-slate-500">
          Existing file uploaded{statusLabel ? ` (${statusLabel})` : ''}
        </p>
      );
    }

    return (
      <div className="mt-1 flex items-start gap-1.5">
        <img
          src={fileUrl}
          alt="Existing document"
          className="h-14 w-20 rounded-md border border-slate-300 bg-slate-50 object-cover"
          loading="lazy"
        />
        {statusLabel ? (
          <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] capitalize text-slate-600">
            {statusLabel}
          </span>
        ) : null}
      </div>
    );
  };

  const fetchEditPartnerDetails = async () => {
    if (!isEditMode || !editPartner?.id) return;

    setLoadingExistingDocs(true);
    try {
      const result = await apiCall({
        endpoint: `partner/onboarding-data/${editPartner.id}`,
        method: 'GET',
      });

      if (result?.status === 200) {
        const partnerData = result?.data?.partner || {};
        const bankPayload = result?.data?.bank_details || {};
        const docs = Array.isArray(result?.data?.documents) ? result.data.documents : [];

        setStaffData((prev) => ({
          ...prev,
          name: partnerData?.name || prev.name,
          mobile: partnerData?.mobile || prev.mobile,
          dob: normalizeDobValue(partnerData?.dob) || prev.dob,
          work: partnerData?.profession || partnerData?.work || prev.work,
          labour_count: partnerData?.team_size
            ? String(partnerData.team_size)
            : partnerData?.labour_count || prev.labour_count,
          area: partnerData?.area || prev.area,
          service_areas: partnerData?.service_areas || partnerData?.area || prev.service_areas,
          aadhaar_no: partnerData?.aadhaar_no || partnerData?.aadhar_no || prev.aadhaar_no,
          pan_no: partnerData?.pan_no || prev.pan_no,
          account_holder_name:
            bankPayload?.account_holder_name ||
            bankPayload?.account_name ||
            partnerData?.account_holder_name ||
            partnerData?.account_name ||
            prev.account_holder_name,
          bank_name: bankPayload?.bank_name || partnerData?.bank_name || prev.bank_name,
          bank_branch: bankPayload?.bank_branch || partnerData?.bank_branch || prev.bank_branch,
          account_number:
            bankPayload?.account_number ||
            bankPayload?.account_no ||
            partnerData?.account_number ||
            partnerData?.account_no ||
            prev.account_number,
          ifsc_code:
            bankPayload?.ifsc_code || bankPayload?.ifsc || partnerData?.ifsc_code || partnerData?.ifsc || prev.ifsc_code,
          upi_id: bankPayload?.upi_id || partnerData?.upi_id || prev.upi_id,
        }));

        const nextDocs = getEmptyExistingDocs();
        docs.forEach((doc) => {
          const key = mapDocTypeToField(doc?.type);
          if (key && doc?.file_path) {
            nextDocs[key] = doc;
          }
        });
        setExistingDocs(nextDocs);
      }
    } catch (error) {
      console.log('Error fetching partner onboarding data:', error);
    } finally {
      setLoadingExistingDocs(false);
    }
  };

  useEffect(() => {
    const selectedPartner = location?.state?.editPartner || null;
    const prefillMobile = location?.state?.prefillMobile || '';

    if (selectedPartner) {
      setStaffData(getInitialStaffData(prefillMobile, selectedPartner));
      setExistingDocs(getEmptyExistingDocs());
      return;
    }

    setExistingDocs(getEmptyExistingDocs());
    if (!prefillMobile) return;
    setStaffData((prev) => ({ ...prev, mobile: prefillMobile }));
  }, [location]);

  useEffect(() => {
    fetchEditPartnerDetails();
  }, [isEditMode, editPartner?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'pan_no' || name === 'ifsc_code' ? value.toUpperCase() : value;
    setStaffData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setStaffData((prev) => ({
      ...prev,
      [name]: files[0],
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const regex = {
      mobile: /^[0-9]{10}$/,
      aadhaar: /^[0-9]{12}$/,
      pan: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    };

    ['name', 'mobile', 'work'].forEach((field) => {
      if (!String(staffData[field] || '').trim()) {
        newErrors[field] = `${field.replace('_', ' ')} is required`;
      }
    });

    if (staffData.mobile && !regex.mobile.test(staffData.mobile)) {
      newErrors.mobile = 'Mobile number must be 10 digits';
    }
    if (staffData.aadhaar_no && !regex.aadhaar.test(staffData.aadhaar_no)) {
      newErrors.aadhaar_no = 'Aadhaar number must be 12 digits';
    }
    if (staffData.pan_no && !regex.pan.test(staffData.pan_no)) {
      newErrors.pan_no = 'Invalid PAN number format';
    }
    if (staffData.account_number && !/^[0-9]{6,20}$/.test(String(staffData.account_number))) {
      newErrors.account_number = 'Account number should be 6 to 20 digits';
    }
    if (staffData.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(String(staffData.ifsc_code).toUpperCase())) {
      newErrors.ifsc_code = 'Invalid IFSC code format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    const appendField = (key, value) => {
      formData.append(key, String(value ?? ''));
    };
    const appendFile = (key, value) => {
      if (value instanceof File) {
        formData.append(key, value);
      }
    };

    appendField('name', staffData.name);
    appendField('mobile', staffData.mobile);
    appendField('dob', staffData.dob);
    appendField('profession', staffData.work);
    appendField('work', staffData.work);
    appendField('team_size', staffData.labour_count);
    appendField('labour_count', staffData.labour_count);
    appendField('area', staffData.area);
    appendField('service_areas', staffData.service_areas);
    appendField('aadhaar_no', staffData.aadhaar_no);
    appendField('aadhar_no', staffData.aadhaar_no);
    appendField('pan_no', staffData.pan_no);
    appendField('account_holder_name', staffData.account_holder_name);
    appendField('account_name', staffData.account_holder_name);
    appendField('bank_name', staffData.bank_name);
    appendField('bank_branch', staffData.bank_branch);
    appendField('account_number', staffData.account_number);
    appendField('account_no', staffData.account_number);
    appendField('ifsc_code', staffData.ifsc_code);
    appendField('ifsc', staffData.ifsc_code);
    appendField('upi_id', staffData.upi_id);

    const derivedAge = calculateAgeFromDob(staffData.dob);
    if (derivedAge) {
      appendField('age', derivedAge);
    }

    appendFile('aadhaar_front', staffData.aadhaar_front);
    appendFile('aadhar_front', staffData.aadhaar_front);
    appendFile('aadhaar_back', staffData.aadhaar_back);
    appendFile('aadhar_back', staffData.aadhaar_back);
    appendFile('pan_card', staffData.pan_card);
    appendFile('address_proof', staffData.address_proof);
    appendFile('photo', staffData.photo);

    if (isEditMode && editPartner?.id) {
      appendField('partner_id', editPartner.id);
      appendField('id', editPartner.id);
    }

    setIsSubmitting(true);
    try {
      const result = await apiCall({
        endpoint: 'partner/register',
        method: 'POST',
        data: formData,
      });

      console.log('API response:', result); 

      if ([200, 201].includes(Number(result?.status)) || result?.status === 'success' || result?.success === true) {
        toast.success(isEditMode ? 'Partner saved successfully' : 'Partner added successfully');
        navigate('/list-partner');
      } else {
        toast.error(result?.message || (isEditMode ? 'Error saving partner' : 'Error adding partner'));
      }
    } catch (error) {
      console.log('Error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'mt-1 block w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="mt-4 w-full">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">
                  {isEditMode ? 'Edit Partner Profile' : 'Add New Partner'}
                </h2>
                <p className="mt-0.5 text-[11px] text-slate-600">
                  {isEditMode
                    ? 'Update partner details from onboarding data.'
                    : 'Fill partner details and upload documents.'}
                </p>
              </div>
              {isEditMode ? (
                <span className="w-fit rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[10px] font-semibold text-blue-700">
                  Edit Mode
                </span>
              ) : null}
            </div>
          </div>

          <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-4 p-4">
            <section className="border-b border-slate-200 pb-4">
              <h3 className="text-xs font-semibold text-slate-800">Basic Information</h3>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="text-[11px] font-semibold text-slate-700">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={staffData.name}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter full name"
                  />
                  {errors.name ? <p className="mt-0.5 text-[11px] text-red-500">{errors.name}</p> : null}
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">
                    Phone No. <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="mobile"
                    value={staffData.mobile}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="0000000000"
                  />
                  {errors.mobile ? <p className="mt-0.5 text-[11px] text-red-500">{errors.mobile}</p> : null}
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">DOB</label>
                  <input
                    type="date"
                    name="dob"
                    value={staffData.dob}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">
                    Work <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="work"
                    value={staffData.work}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Profession / Work type"
                  />
                  {errors.work ? <p className="mt-0.5 text-[11px] text-red-500">{errors.work}</p> : null}
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Labour Count</label>
                  <input
                    type="number"
                    name="labour_count"
                    value={staffData.labour_count}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Team size"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Area</label>
                  <input
                    type="text"
                    name="area"
                    value={staffData.area}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Primary area"
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-3">
                  <label className="text-[11px] font-semibold text-slate-700">Service Areas</label>
                  <input
                    type="text"
                    name="service_areas"
                    value={staffData.service_areas}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Comma separated service areas"
                  />
                </div>
              </div>
            </section>

            <section className="border-b border-slate-200 pb-4">
              <h3 className="text-xs font-semibold text-slate-800">Identity Information</h3>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Aadhaar Number</label>
                  <input
                    type="text"
                    name="aadhaar_no"
                    value={staffData.aadhaar_no}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="12 digit Aadhaar number"
                  />
                  {errors.aadhaar_no ? <p className="mt-0.5 text-[11px] text-red-500">{errors.aadhaar_no}</p> : null}
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">PAN Number</label>
                  <input
                    type="text"
                    name="pan_no"
                    value={staffData.pan_no}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="PAN number"
                  />
                  {errors.pan_no ? <p className="mt-0.5 text-[11px] text-red-500">{errors.pan_no}</p> : null}
                </div>
              </div>
            </section>

            <section className="border-b border-slate-200 pb-4">
              <h3 className="text-xs font-semibold text-slate-800">Bank Information</h3>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Account Holder Name</label>
                  <input
                    type="text"
                    name="account_holder_name"
                    value={staffData.account_holder_name}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Account holder name"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Bank Name</label>
                  <input
                    type="text"
                    name="bank_name"
                    value={staffData.bank_name}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Bank name"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Bank Branch</label>
                  <input
                    type="text"
                    name="bank_branch"
                    value={staffData.bank_branch}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Bank branch"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Account Number</label>
                  <input
                    type="text"
                    name="account_number"
                    value={staffData.account_number}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Account number"
                  />
                  {errors.account_number ? (
                    <p className="mt-0.5 text-[11px] text-red-500">{errors.account_number}</p>
                  ) : null}
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">IFSC Code</label>
                  <input
                    type="text"
                    name="ifsc_code"
                    value={staffData.ifsc_code}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="IFSC code"
                  />
                  {errors.ifsc_code ? <p className="mt-0.5 text-[11px] text-red-500">{errors.ifsc_code}</p> : null}
                </div>

                <div className="md:col-span-2 lg:col-span-1">
                  <label className="text-[11px] font-semibold text-slate-700">UPI ID</label>
                  <input
                    type="text"
                    name="upi_id"
                    value={staffData.upi_id}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="name@upi"
                  />
                </div>
              </div>
            </section>

            <section className="border-b border-slate-200 pb-4">
              <h3 className="text-xs font-semibold text-slate-800">Documents</h3>
              <p className="mt-0.5 text-[10px] text-slate-500">
                Existing files are shown below each field in edit mode.
              </p>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Aadhaar Front</label>
                  <input
                    type="file"
                    name="aadhaar_front"
                    onChange={handleFileChange}
                    className="mt-1 block w-full cursor-pointer rounded-md border border-slate-300 px-2 py-1 text-[10px] text-slate-700 file:mr-1.5 file:rounded file:border-0 file:bg-slate-100 file:px-1.5 file:py-0.5 file:text-[10px] file:font-medium file:text-slate-700"
                  />
                  {renderExistingDoc('aadhaar_front')}
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Aadhaar Back</label>
                  <input
                    type="file"
                    name="aadhaar_back"
                    onChange={handleFileChange}
                    className="mt-1 block w-full cursor-pointer rounded-md border border-slate-300 px-2 py-1 text-[10px] text-slate-700 file:mr-1.5 file:rounded file:border-0 file:bg-slate-100 file:px-1.5 file:py-0.5 file:text-[10px] file:font-medium file:text-slate-700"
                  />
                  {renderExistingDoc('aadhaar_back')}
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">PAN File</label>
                  <input
                    type="file"
                    name="pan_card"
                    onChange={handleFileChange}
                    className="mt-1 block w-full cursor-pointer rounded-md border border-slate-300 px-2 py-1 text-[10px] text-slate-700 file:mr-1.5 file:rounded file:border-0 file:bg-slate-100 file:px-1.5 file:py-0.5 file:text-[10px] file:font-medium file:text-slate-700"
                  />
                  {renderExistingDoc('pan_card')}
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-slate-700">Address Proof</label>
                  <input
                    type="file"
                    name="address_proof"
                    onChange={handleFileChange}
                    className="mt-1 block w-full cursor-pointer rounded-md border border-slate-300 px-2 py-1 text-[10px] text-slate-700 file:mr-1.5 file:rounded file:border-0 file:bg-slate-100 file:px-1.5 file:py-0.5 file:text-[10px] file:font-medium file:text-slate-700"
                  />
                  {renderExistingDoc('address_proof')}
                </div>

                <div className="md:col-span-2 xl:col-span-1">
                  <label className="text-[11px] font-semibold text-slate-700">Photo</label>
                  <input
                    type="file"
                    name="photo"
                    onChange={handleFileChange}
                    className="mt-1 block w-full cursor-pointer rounded-md border border-slate-300 px-2 py-1 text-[10px] text-slate-700 file:mr-1.5 file:rounded file:border-0 file:bg-slate-100 file:px-1.5 file:py-0.5 file:text-[10px] file:font-medium file:text-slate-700"
                  />
                  {renderExistingDoc('photo')}
                </div>
              </div>
            </section>

            <div className="flex flex-col-reverse gap-1.5 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={() => navigate('/list-partner')}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
              >
                {isSubmitting ? (isEditMode ? 'Saving...' : 'Adding...') : isEditMode ? 'Save Partner' : 'Add Partner'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddPartner;
